import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  CCol, CRow, CButton, CAlert,
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter, CSpinner,
  CCard, CCardHeader, CCardBody,
  CTabs, CTabList, CTab, CTabPanel,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import {
  cilFile, cilUser, cilSettings, cilDollar, cilCalendar, cilPencil,
  cilArrowLeft, cilPen, cilTrash, cilWarning,
} from '@coreui/icons'
import { api, currentUser } from '../api/client'
import { fmtMoneda } from '../utils/moneda'
import WwGrid, { WwColumn } from '../components/ww/WwGrid'
import { usePendiente } from '../components/ww/pendiente'

const columnasAbonos: WwColumn[] = [
  { key: 'abonoNro', title: 'Nro', type: 'number' },
  { key: 'abonoFecha', title: 'Fecha', type: 'date' },
  { key: 'abonoTipoAbono', title: 'Tipo Abono' },
  { key: 'abonoValor', title: 'Valor', type: 'currency' },
  { key: 'abonoCondenas', title: 'Condenas', type: 'number' },
  { key: 'abonoPrestamos', title: 'Préstamos', type: 'number' },
  { key: 'abonoLibreValor1', title: 'Libre Valor 1', type: 'currency' },
]

const columnasAtrasos: WwColumn[] = [
  { key: 'atrazoNro', title: 'Nro', type: 'number' },
  { key: 'atrazoFecha', title: 'Fecha', type: 'date' },
  { key: 'atrazoHora', title: 'Hora' },
  { key: 'prestamoProximoCobro', title: 'Próximo Cobro', type: 'date' },
  { key: 'atrazoObservacion', title: 'Observación' },
  { key: 'atrazoCreadoPor', title: 'Creado Por' },
]

const modalidadTexto = (m: any): string => {
  const textos: Record<number, string> = { 1: 'Diario', 7: 'Semanal', 15: 'Quincenal', 30: 'Mensual', 60: 'Pago Libre' }
  return textos[Number(m)] ?? (m === null || m === undefined || m === '' ? '—' : String(m))
}

// Moneda: fuente única utils/moneda.ts (fmtMoneda — símbolo configurado por
// empresa + máscara GX "$ Z,ZZZ,ZZZ,ZZ9.99", aticket.cs:222). [RGM] 2026-10-02 (F)

const boolTexto = (v: any): string => {
  if (v === null || v === undefined || v === '') return '—'
  return Number(v) ? 'Sí' : 'No'
}

const PrestamoViewPage: React.FC = () => {
  const { nro } = useParams<{ nro: string }>()
  const navigate = useNavigate()
  const user = currentUser()
  const empresa = user?.companyId ?? '01'
  const [prestamo, setPrestamo] = useState<any>(null)
  const [abonos, setAbonos] = useState<any[]>([])
  const [atrasos, setAtrasos] = useState<any[]>([])
  const [cargando, setCargando] = useState(true)
  const [cargandoHijas, setCargandoHijas] = useState(true)
  const [error, setError] = useState('')
  const [eliminarAbierto, setEliminarAbierto] = useState(false)
  const { aviso, setAviso } = usePendiente()
  // Contadores de los tabs — SIEMPRE declarados ANTES de los early returns
  // (React #310: un useState después de un return condicional rompe el orden
  // de hooks y deja la vista en BLANCO — reporte Rafael 2026-10-01).
  const [totalAbonos, setTotalAbonos] = useState(0)
  const [totalAtrasos, setTotalAtrasos] = useState(0)

  useEffect(() => {
    setCargando(true)
    setError('')
    api.get<any[]>(`/prestamos?empresa=${empresa}`)
      .then((lista) => {
        const encontrado = (lista ?? []).find((p) => String(p.prestamoNro ?? '') === String(nro ?? ''))
        if (!encontrado) setError('Préstamo no encontrado')
        setPrestamo(encontrado ?? null)
      })
      .catch((e: any) => setError(e.message ?? 'Préstamo no encontrado'))
      .finally(() => setCargando(false))
  }, [nro, empresa])

  useEffect(() => {
    if (!nro) return
    setCargandoHijas(true)
    Promise.all([
      api.get<any[]>(`/prestamos/${nro}/abonos?empresa=${empresa}`),
      api.get<any[]>(`/atrasos?empresa=${empresa}`),
    ])
      .then(([abs, atrs]) => {
        setAbonos(abs ?? [])
        setAtrasos((atrs ?? []).filter((a) => String(a.prestamoNro ?? '') === String(nro ?? '')))
      })
      .catch((e: any) => setError(e.message))
      .finally(() => setCargandoHijas(false))
  }, [nro, empresa])

  const v = (valor: any): string => (valor === null || valor === undefined || valor === '' ? '—' : String(valor))

  const fila = (label: string, valor: any) => (
    <CCol md={6}>
      <div className="small text-body-secondary">{label}</div>
      <div className="fw-medium text-break">{valor ?? '—'}</div>
    </CCol>
  )

  const tarjeta = (icono: any, titulo: string, contenido: React.ReactNode) => (
    <CCard className="mb-3 shadow-sm" style={{ borderRadius: 8 }}>
      <CCardHeader className=" d-flex align-items-center gap-2 fw-semibold border-bottom py-2">
        <CIcon icon={icono} size="sm" /> {titulo}
      </CCardHeader>
      <CCardBody style={{ padding: '14px 16px' }}>
        <CRow className="g-3">{contenido}</CRow>
      </CCardBody>
    </CCard>
  )

  // [RGM] 2026-10-01 (reporte Rafael "Modificar no funciona en el view"):
  // la edición real vive en el modal de PrestamosPage (handleEdit + PUT) —
  // se navega con ?editar=<nro> y PrestamosPage abre ese préstamo en edición.
  const modificar = () => {
    navigate(`/prestamos?editar=${nro}`)
  }

  // [RGM] 2026-10-02 (reporte Rafael "Eliminar no está funcional"): el endpoint
  // DELETE existe y restaura la caja (PrestamosController.Eliminar) — aquí se
  // cablea de verdad; el mensaje anterior ("no existe endpoint") era obsoleto.
  const confirmarEliminar = () => {
    setEliminarAbierto(false)
    api.delete(`/prestamos/${nro}?empresa=${empresa}`)
      .then(() => {
        navigate('/prestamos')
      })
      .catch((err: any) => setError(err.message ?? 'Error al eliminar'))
  }

  if (cargando) {
    return (
      <div className="text-center py-5">
        <CSpinner color="primary" />
      </div>
    )
  }

  if (error && !prestamo) {
    return (
      <>
        <CRow className="mb-4">
          <CCol className="d-flex align-items-center justify-content-between">
            <h4 className="mb-0">Préstamo</h4>
            <CButton color="secondary" variant="outline" onClick={() => navigate('/prestamos')}>
              <CIcon icon={cilArrowLeft} className="me-1" size="sm" />
              Volver
            </CButton>
          </CCol>
        </CRow>
        <CAlert color="warning">Préstamo no encontrado</CAlert>
      </>
    )
  }

  return (
    <>
      <CRow className="mb-4">
        <CCol className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <h4 className="mb-0">Préstamo {nro}</h4>
          <small className="text-body-secondary">{totalAbonos + totalAtrasos} {totalAbonos + totalAtrasos === 1 ? 'registro' : 'registros'}</small>
          </div>
          <div className="d-flex gap-2">
            <CButton color="secondary" variant="outline" onClick={() => navigate('/prestamos')}>
              <CIcon icon={cilArrowLeft} className="me-1" size="sm" />
              Volver
            </CButton>
            <CButton color="primary" onClick={modificar}>
              <CIcon icon={cilPen} className="me-1" size="sm" />
              Modificar
            </CButton>
            <CButton color="danger" onClick={() => setEliminarAbierto(true)}>
              <CIcon icon={cilTrash} className="me-1" size="sm" />
              Eliminar
            </CButton>
          </div>
        </CCol>
      </CRow>

      {error && <CAlert color="danger">{error}</CAlert>}
      {aviso && <CAlert color="warning" dismissible onClose={() => setAviso('')}>{aviso}</CAlert>}

      {tarjeta(cilFile, 'Información General', (
        <>
          {fila('Nro', prestamo?.prestamoNro)}
          {fila('Fecha', v(prestamo?.prestamoFecha).slice(0, 10))}
          {fila('Tipo', prestamo?.prestamoTipo)}
          {fila('Creado por', prestamo?.prestamoCreadoPor)}
        </>
      ))}

      {tarjeta(cilUser, 'Cliente', (
        <>
          {fila('Cédula', prestamo?.clienteCedula)}
          {fila('Nombre', prestamo?.clienteNombre)}
          {fila('Dirección', prestamo?.clienteDireccion)}
          {fila('WhatsApp', prestamo?.whatsApp)}
          {fila('Ruta', prestamo?.ruta)}
          {fila('Cobrador', prestamo?.cobrador)}
        </>
      ))}

      {tarjeta(cilSettings, 'Configuración', (
        <>
          {fila('Modalidad', modalidadTexto(prestamo?.prestamoMPago))}
          {fila('Día Cobro', prestamo?.prestamoDiaCobro)}
          {fila('Solicita Renovar', boolTexto(prestamo?.solicitaRenovar))}
        </>
      ))}

      {tarjeta(cilDollar, 'Valores', (
        <>
          {fila('Valor', fmtMoneda(prestamo?.prestamoValor))}
          {fila('Cuotas', prestamo?.prestamoCuotas)}
          {fila('Cuota', fmtMoneda(prestamo?.prestamoCuota))}
          {fila('Utilidad', fmtMoneda(prestamo?.prestamoUtilidad))}
          {fila('Total', fmtMoneda(prestamo?.total))}
          {fila('Abonos', fmtMoneda(prestamo?.prestamoAbonos))}
          {fila('Saldo', fmtMoneda(prestamo?.prestamoSaldo))}
          {fila('Saldo Condenas', fmtMoneda(prestamo?.saldoCondenas))}
          {fila('Saldo Real', fmtMoneda(prestamo?.saldoReal))}
          {fila('Saldo Pico', fmtMoneda(prestamo?.saldoPico))}
        </>
      ))}

      {tarjeta(cilCalendar, 'Fechas', (
        <>
          {fila('Próximo Cobro', v(prestamo?.prestamoProximoCobro).slice(0, 10))}
          {fila('Último Abono', v(prestamo?.prestamoFechaUltimoAbono).slice(0, 10))}
          {fila('Termina', v(prestamo?.prestamoFechaTermina).slice(0, 10))}
        </>
      ))}

      {tarjeta(cilPencil, 'Observación', (
        <>
          <CCol md={12}>
            <div className="small text-body-secondary">Observación</div>
            <div className="fw-medium text-break">{v(prestamo?.prestamoObservacion)}</div>
          </CCol>
        </>
      ))}

      <CTabs defaultActiveItemKey="abonos">
        <CTabList variant="tabs">
          <CTab itemKey="abonos">Abonos</CTab>
          <CTab itemKey="atrasos">Atrasos</CTab>
        </CTabList>
        <CTabPanel itemKey="abonos" className="pt-3">
          {cargandoHijas ? (
            <div className="text-center py-4">
              <CSpinner color="primary" />
            </div>
          ) : (
            <WwGrid title="Abonos" columns={columnasAbonos} rows={abonos}
        onTotalChange={setTotalAbonos} onAgregar={() => navigate('/abonos')} />
          )}
        </CTabPanel>
        <CTabPanel itemKey="atrasos" className="pt-3">
          {cargandoHijas ? (
            <div className="text-center py-4">
              <CSpinner color="primary" />
            </div>
          ) : (
            <WwGrid title="Atrasos" columns={columnasAtrasos} rows={atrasos}
        onTotalChange={setTotalAtrasos} onAgregar={() => navigate('/atrasos')} />
          )}
        </CTabPanel>
      </CTabs>

      <CModal visible={eliminarAbierto} onClose={() => setEliminarAbierto(false)}>
        <CModalHeader>
          <CModalTitle>
            <CIcon icon={cilWarning} className="me-1 text-danger" />
            Eliminar préstamo
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p className="mb-0">
            ¿Eliminar el préstamo <strong>{nro}</strong>?
          </p>
        </CModalBody>
        <CModalFooter className="border-top">
          <CButton color="secondary" type="button" onClick={() => setEliminarAbierto(false)}>Cancelar</CButton>
          <CButton color="danger" type="button" onClick={confirmarEliminar}>
            Eliminar
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  )
}

export default PrestamoViewPage
